import { RefObject, useRef } from 'react'
import axios from 'axios'
import { FormikProps, setNestedObjectValues } from 'formik'
import extractServerErrors from '../extractServerErrors'
import submitNavigationForm from './submitNavigationForm'
import { PublishMode } from './types'

interface Options<Values> {
  formikRef: RefObject<FormikProps<Values>>
  publishUrl: (id: number) => string
  publishAndSyncUrl?: (id: number) => string
  publishFailedText: string
  onPublishError: (messages: string[]) => void
}

// "Save and publish": the form is validated and saved first, then the saved record is
// published (XHR + reload) or published and synced (full navigation, so the flash survives).
const useSaveAndPublish = <Values>({
  formikRef,
  publishUrl,
  publishAndSyncUrl,
  publishFailedText,
  onPublishError
}: Options<Values>) => {
  const pendingMode = useRef<PublishMode | null>(null)

  const confirmPublish = (mode: PublishMode) => {
    const formik = formikRef.current
    if (!formik) return

    formik.validateForm().then((errors) => {
      if (Object.keys(errors).length > 0) {
        formik.setTouched(setNestedObjectValues(errors, true))
        return
      }
      pendingMode.current = mode
      formik.submitForm()
    })
  }

  const afterSaved = (savedId: number): boolean => {
    const mode = pendingMode.current
    pendingMode.current = null
    if (mode === 'publish') {
      axios
        .post(publishUrl(savedId))
        .then(() => window.location.reload())
        .catch((error) =>
          onPublishError(extractServerErrors(error, publishFailedText))
        )
      return true
    }
    if (mode === 'publish_and_sync' && publishAndSyncUrl) {
      submitNavigationForm(publishAndSyncUrl(savedId), 'post')
      return true
    }
    return false
  }

  const cancelPending = () => {
    pendingMode.current = null
  }

  return { confirmPublish, afterSaved, cancelPending }
}

export default useSaveAndPublish
